import Logger from "./logger.service";
import { store } from "../context/store";

/**
 * Audio Device Handler for Chrome
 * Handles microphone reconnection when earphones are connected/disconnected
 * Fixes the issue with microphone device changes in Chrome browser
 */
class AudioDeviceHandler {
    constructor(userAgent) {
        this.userAgent = userAgent;
        this.currentSession = null;
        this.mediaStream = null;
        this.deviceChangeListenerAdded = false;
        this.isChrome = this._isChromeBrowser();

        // Only setup listeners if it's Chrome browser
        if (this.isChrome) {
            this._setupUserAgentListeners();
            Logger.info("Chrome detected, audio device handler initialized");
        }
    }

    _isChromeBrowser() {
        return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    }

    _setupUserAgentListeners() {
        // Listen for new sessions
        this.userAgent.on('newRTCSession', (data) => {
            const session = data.session;

            // Store the current session
            this.currentSession = session;

            // Set up session event listeners
            this._setupSessionListeners(session);
        });
    }

    _setupSessionListeners(session) {
        // When the session is confirmed (call answered)
        session.on('confirmed', () => {
            Logger.info('Call confirmed, setting up device change listeners');

            // Get the local media stream
            this.mediaStream = session.connection.getLocalStreams()[0];

            // Set up the device change listener if not already added
            if (!this.deviceChangeListenerAdded) {
                this._setupDeviceChangeListener();
                this.deviceChangeListenerAdded = true;
            }
        });

        // When the session ends
        session.on('ended', () => {
            // Stop all tracks to properly release microphone
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach(track => {
                    track.stop();
                    Logger.info(`Track ${track.kind} stopped and released`);
                });
            }

            this.currentSession = null;
            this.mediaStream = null;

            // Cleanup if needed
            Logger.info('Call ended, cleaning up audio device handler and releasing microphone');
        });

        // When the session fails
        session.on('failed', () => {
            // Stop all tracks to properly release microphone
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach(track => {
                    track.stop();
                    Logger.info(`Track ${track.kind} stopped and released`);
                });
            }

            this.currentSession = null;
            this.mediaStream = null;

            // Cleanup if needed
            Logger.info('Call failed, cleaning up audio device handler and releasing microphone');
        });
    }

    _setupDeviceChangeListener() {
        // Listen for device changes
        navigator.mediaDevices.addEventListener('devicechange', this._handleDeviceChange.bind(this));
        Logger.info('Device change listener set up for Chrome');
    }

    async _handleDeviceChange() {
        Logger.info('Audio device change detected in Chrome');

        // Only proceed if we have an active session
        if (!this.currentSession || !this.mediaStream) {
            Logger.info('No active session, ignoring device change');
            return;
        }

        try {
            // Get the updated list of audio devices
            const devices = await navigator.mediaDevices.enumerateDevices();

            // Handle input device changes (microphone)
            await this._handleInputDeviceChange(devices);

            // Handle output device changes (speakers/earphones)
            await this._handleOutputDeviceChange();

        } catch (error) {
            Logger.error('Error handling device change:', error);
        }
    }

    async _handleInputDeviceChange(devices) {
        // If we have an active call with media
        if (this.currentSession && this.currentSession.isEstablished() && this.mediaStream) {
            Logger.info('Updating microphone input in Chrome');

            try {
                // Get default audio input device or first available
                const audioInputDevices = devices.filter(device => device.kind === 'audioinput');
                const defaultDevice = audioInputDevices.find(device => device.deviceId === 'default') || audioInputDevices[0];

                if (!defaultDevice) {
                    Logger.warn('No audio input devices found');
                    return;
                }

                // Get updated audio constraints
                const constraints = {
                    audio: {
                        deviceId: { exact: defaultDevice.deviceId },
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true
                    },
                    video: false
                };

                // Get new stream with updated device
                const newStream = await navigator.mediaDevices.getUserMedia(constraints);

                // Replace the track in the current session
                const audioTrack = newStream.getAudioTracks()[0];

                const sender = this.currentSession.connection.getSenders()
                    .find(sender => sender.track && sender.track.kind === 'audio');

                if (sender) {
                    await sender.replaceTrack(audioTrack);
                    Logger.info('Audio track replaced successfully in Chrome');

                    // Stop old tracks
                    this.mediaStream.getAudioTracks().forEach(track => track.stop());

                    // Update the stored media stream
                    this.mediaStream = newStream;
                }
            } catch (error) {
                Logger.error('Error updating microphone input in Chrome:', error);
            }
        }
    }

    async _handleOutputDeviceChange() {
        // Handle the output device change for the callAudio element
        const audioElement = window.callAudioElement;

        if (audioElement && typeof audioElement.setSinkId !== 'undefined') {
            try {
                const callAudio = store.getState().toolbar.callAudoSkinId;
                if (callAudio && callAudio.deviceId) {
                    await audioElement.setSinkId(callAudio.deviceId);
                    Logger.info("Audio output device updated in Chrome");
                }
            } catch (error) {
                Logger.error('Error updating audio output device in Chrome:', error);
            }
        }
    }

    // Method to manually trigger device refresh
    async refreshAudioDevices() {
        Logger.info('Manually refreshing audio devices in Chrome');
        await this._handleDeviceChange();
    }

    // Helper to release microphone for a call
    static releaseMicrophone(call) {
        if (!call || !call.connection) return;

        try {
            if (/Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)) {
                if (call.connection && call.connection.getLocalStreams) {
                    const localStreams = call.connection.getLocalStreams();
                    localStreams.forEach(stream => {
                        stream.getTracks().forEach(track => {
                            track.stop();
                            Logger.info(`Released ${track.kind} track after call ended/failed`);
                        });
                    });
                }
            }
        } catch (error) {
            Logger.error('Error releasing microphone:', error);
        }
    }
}

export default AudioDeviceHandler;